import {
  completeAgenticOAuth,
  disconnectAgenticOAuth,
  fetchAgenticSnapshot,
  fetchAgenticStatus,
  startAgenticOAuth,
  updateAgenticSettings,
  type AgenticSettings,
} from "../api/agenticClient";
import { isRobinhoodIntegrationEnabled } from "./agenticFeatures";
import { notifyRobinhoodStatusChanged } from "./robinhoodStatus";

export const AGENTIC_SETTINGS_CHANGED_EVENT = "sfe:agentic-settings-changed";
export const AGENTIC_SNAPSHOT_REFRESHED_EVENT = "sfe:agentic-snapshot-refreshed";

export interface RobinhoodRefreshContext {
  settings: AgenticSettings;
  connected: boolean;
  nextAutoRefreshSec: number | null;
  snapshotRefreshedAt: string | null;
}

const DEFAULT_SETTINGS: AgenticSettings = {
  snapshotAutoRefreshEnabled: true,
  snapshotAutoRefreshMinutes: 7,
  autoImportEnabled: false,
  autoImportLastRunAt: null,
};

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function formatExpiresIn(sec: number): string {
  if (sec >= 86400) return `${Math.ceil(sec / 86400)}d`;
  if (sec >= 3600) return `${Math.ceil(sec / 3600)}h`;
  if (sec >= 60) return `${Math.ceil(sec / 60)}m`;
  return `${Math.max(0, sec)}s`;
}

export function notifyAgenticSettingsChanged(): void {
  window.dispatchEvent(new CustomEvent(AGENTIC_SETTINGS_CHANGED_EVENT));
}

export function notifyAgenticSnapshotRefreshed(refreshedAt: string | null): void {
  window.dispatchEvent(
    new CustomEvent(AGENTIC_SNAPSHOT_REFRESHED_EVENT, { detail: { refreshedAt } }),
  );
}

function readSnapshotRefreshedAt(
  snapshot: { refreshedAt?: string | null; capturedAt?: string | null } | null | undefined,
): string | null {
  if (!snapshot) return null;
  if (typeof snapshot.refreshedAt === "string" && snapshot.refreshedAt) {
    return snapshot.refreshedAt;
  }
  if (typeof snapshot.capturedAt === "string" && snapshot.capturedAt) {
    return snapshot.capturedAt;
  }
  return null;
}

export async function loadRobinhoodRefreshContext(): Promise<RobinhoodRefreshContext> {
  if (!isRobinhoodIntegrationEnabled()) {
    return {
      settings: DEFAULT_SETTINGS,
      connected: false,
      nextAutoRefreshSec: null,
      snapshotRefreshedAt: null,
    };
  }
  try {
    const [status, snapshotRes] = await Promise.all([
      fetchAgenticStatus().catch(() => ({ connected: false } as { connected: boolean })),
      fetchAgenticSnapshot().catch(() => null),
    ]);
    return {
      settings: snapshotRes?.settings ?? DEFAULT_SETTINGS,
      connected: Boolean(status.connected),
      nextAutoRefreshSec: snapshotRes?.nextAutoRefreshSec ?? null,
      snapshotRefreshedAt: readSnapshotRefreshedAt(snapshotRes?.snapshot),
    };
  } catch {
    return {
      settings: DEFAULT_SETTINGS,
      connected: false,
      nextAutoRefreshSec: null,
      snapshotRefreshedAt: null,
    };
  }
}

export function robinhoodRefreshCountdownText(ctx: RobinhoodRefreshContext): string {
  if (!ctx.settings.snapshotAutoRefreshEnabled) return "Robinhood refresh is off";
  if (!ctx.connected) return "Connect Robinhood to enable server refresh";
  if (ctx.nextAutoRefreshSec === 0) return "Due now — proxy will refresh shortly";
  return `Next refresh in ${formatExpiresIn(ctx.nextAutoRefreshSec ?? 0)}`;
}

export function robinhoodAccountConnectionHtml(ctx: RobinhoodRefreshContext): string {
  return `
    <div class="sfe-rh-account" data-sfe-rh-account>
      <p class="muted small">This login keeps its own Robinhood connection. The closed-market feed uses only this connection.</p>
      <p class="muted small" data-sfe-rh-account-status>${ctx.connected ? "Connected" : "Not connected"}</p>
      <div class="row sfe-rh-account-actions">
        <button type="button" class="small" data-sfe-rh-connect${ctx.connected ? " hidden" : ""}>Connect Robinhood</button>
        <button type="button" class="small ghost" data-sfe-rh-disconnect${ctx.connected ? "" : " hidden"}>Disconnect</button>
      </div>
      <div data-sfe-rh-pending hidden>
        <p class="muted small">After Allow, copy the full address-bar URL from the Robinhood page and paste it here.</p>
        <p class="row"><a class="small" data-sfe-rh-auth-link target="_blank" rel="noopener noreferrer" hidden>Open Robinhood authorization</a></p>
        <textarea data-sfe-rh-callback rows="3" spellcheck="false" placeholder="http://127.0.0.1:33418/callback?code=…"></textarea>
        <button type="button" class="small" data-sfe-rh-complete>Complete connection</button>
      </div>
    </div>`;
}

export function robinhoodRefreshControlsHtml(ctx: RobinhoodRefreshContext): string {
  return `
    <div class="sfe-rh-refresh-settings">
      <label class="sfe-rh-refresh-toggle">
        <input type="checkbox" data-rh-refresh-enabled${ctx.settings.snapshotAutoRefreshEnabled ? " checked" : ""} />
        <span>Enabled</span>
      </label>
      <label class="sfe-rh-refresh-interval">
        <span class="small muted">Every</span>
        <input
          type="number"
          min="1"
          max="120"
          step="1"
          data-rh-refresh-minutes
          value="${ctx.settings.snapshotAutoRefreshMinutes}"
        />
        <span class="small muted">minutes</span>
      </label>
      <p class="muted small sfe-rh-refresh-countdown" data-rh-refresh-countdown role="status">${esc(robinhoodRefreshCountdownText(ctx))}</p>
    </div>`;
}

export function robinhoodRefreshWebSectionHtml(
  ctx: RobinhoodRefreshContext,
  opts: { nested?: boolean } = {},
): string {
  const body = `
      ${robinhoodAccountConnectionHtml(ctx)}
      <p class="muted small sfe-column-settings-lead">Server pulls this login's Robinhood snapshot on a timer while connected.</p>
      ${robinhoodRefreshControlsHtml(ctx)}`;
  if (opts.nested) {
    return `
    <div class="sfe-rh-settings-block">
      <h5 class="sfe-rh-settings-subtitle">Snapshot refresh</h5>
      ${body}
    </div>`;
  }
  return `
    <section class="sfe-web-settings-card">
      <h4 class="sfe-web-settings-card-title">Robinhood refresh</h4>
      ${body}
    </section>`;
}

export function robinhoodRefreshMobileSectionHtml(
  ctx: RobinhoodRefreshContext,
  collapsibleAttrs: string,
): string {
  return `
    <details class="wheel-settings-section sfe-mobile-settings-section sfe-mobile-settings-collapsible" data-sfe-collapsible="settings-rh-refresh" data-sfe-collapsible-default="closed"${collapsibleAttrs}>
      <summary class="sfe-mobile-settings-section-summary">
        <span class="wheel-settings-section-title">Robinhood refresh</span>
      </summary>
      <div class="sfe-mobile-settings-section-body" data-sfe-rh-refresh-host>
        ${robinhoodAccountConnectionHtml(ctx)}
        <p class="sfe-mobile-status">Server pulls this login's Robinhood snapshot on a timer while connected.</p>
        ${robinhoodRefreshControlsHtml(ctx)}
      </div>
    </details>`;
}

function readRobinhoodRefreshInputs(host: ParentNode): AgenticSettings {
  const enabled =
    host.querySelector<HTMLInputElement>("[data-rh-refresh-enabled]")?.checked ?? true;
  const minutesRaw = Number(
    host.querySelector<HTMLInputElement>("[data-rh-refresh-minutes]")?.value ?? "7",
  );
  const minutes = Number.isFinite(minutesRaw)
    ? Math.min(120, Math.max(1, Math.round(minutesRaw)))
    : 7;
  return {
    snapshotAutoRefreshEnabled: enabled,
    snapshotAutoRefreshMinutes: minutes,
  };
}

function updateRobinhoodRefreshCountdown(host: ParentNode, ctx: RobinhoodRefreshContext): void {
  const el = host.querySelector<HTMLElement>("[data-rh-refresh-countdown]");
  if (el) el.textContent = robinhoodRefreshCountdownText(ctx);
}

export function bindRobinhoodRefreshSettings(
  host: ParentNode,
  ctxRef: { current: RobinhoodRefreshContext },
  options: { onError?: (message: string) => void; onConnectionChanged?: () => void } = {},
): () => void {
  let saveTimer: ReturnType<typeof setTimeout> | null = null;

  const save = async (): Promise<void> => {
    const inputs = readRobinhoodRefreshInputs(host);
    try {
      const settings = await updateAgenticSettings(inputs);
      const snapshot = await fetchAgenticSnapshot().catch(() => null);
      ctxRef.current = {
        settings,
        connected: ctxRef.current.connected,
        nextAutoRefreshSec: snapshot?.nextAutoRefreshSec ?? null,
        snapshotRefreshedAt:
          readSnapshotRefreshedAt(snapshot?.snapshot) ?? ctxRef.current.snapshotRefreshedAt,
      };
      notifyAgenticSettingsChanged();
      updateRobinhoodRefreshCountdown(host, ctxRef.current);
    } catch (err) {
      options.onError?.(err instanceof Error ? err.message : "Failed to save Robinhood refresh settings");
    }
  };

  const queueSave = (): void => {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      saveTimer = null;
      void save();
    }, 400);
  };

  host.querySelector<HTMLInputElement>("[data-rh-refresh-enabled]")?.addEventListener("change", queueSave);
  host.querySelector<HTMLInputElement>("[data-rh-refresh-minutes]")?.addEventListener("change", queueSave);

  host.querySelector<HTMLButtonElement>("[data-sfe-rh-connect]")?.addEventListener("click", () => {
    void (async () => {
      try {
        const started = await startAgenticOAuth(window.location.pathname);
        if (started.connected) {
          notifyRobinhoodStatusChanged();
          options.onConnectionChanged?.();
          return;
        }
        const pending = host.querySelector<HTMLElement>("[data-sfe-rh-pending]");
        const link = host.querySelector<HTMLAnchorElement>("[data-sfe-rh-auth-link]");
        if (pending) pending.hidden = false;
        if (link && started.authorizationUrl) {
          link.href = started.authorizationUrl;
          link.hidden = false;
        }
      } catch (err) {
        options.onError?.(err instanceof Error ? err.message : "Could not start Robinhood sign-in");
      }
    })();
  });

  host.querySelector<HTMLButtonElement>("[data-sfe-rh-complete]")?.addEventListener("click", () => {
    void (async () => {
      const callback = host.querySelector<HTMLTextAreaElement>("[data-sfe-rh-callback]")?.value.trim() ?? "";
      if (!callback) {
        options.onError?.("Paste the Robinhood callback URL");
        return;
      }
      try {
        await completeAgenticOAuth(callback);
        notifyRobinhoodStatusChanged();
        notifyAgenticSettingsChanged();
        options.onConnectionChanged?.();
      } catch (err) {
        options.onError?.(err instanceof Error ? err.message : "Could not finish Robinhood sign-in");
      }
    })();
  });

  host.querySelector<HTMLButtonElement>("[data-sfe-rh-disconnect]")?.addEventListener("click", () => {
    void (async () => {
      try {
        await disconnectAgenticOAuth();
        notifyRobinhoodStatusChanged();
        options.onConnectionChanged?.();
      } catch (err) {
        options.onError?.(err instanceof Error ? err.message : "Could not disconnect Robinhood");
      }
    })();
  });

  const countdownTimer = window.setInterval(() => {
    const ctx = ctxRef.current;
    if (ctx.nextAutoRefreshSec !== null && ctx.nextAutoRefreshSec > 0) {
      ctxRef.current = { ...ctx, nextAutoRefreshSec: ctx.nextAutoRefreshSec - 1 };
    }
    updateRobinhoodRefreshCountdown(host, ctxRef.current);
  }, 1000);

  return () => {
    if (saveTimer) clearTimeout(saveTimer);
    window.clearInterval(countdownTimer);
  };
}
